import React, { useEffect, useRef, useState } from 'react';

import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { Card, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { getOrdersToReturn } from './services';

import useRefreshOrder from '../../hooks/useRefreshOrders';
import getEmojiTier from '../../utils/getEmojiTier';

import {
  setOrdersModalConfig,
  setOrdersToReturnFilters,
  setToReturnCounter,
} from './orderSlice';

import FilterSelect from '../common/_inputs/FilterSelect.js';
import OrderCard from './OrderCard';
import { useTranslation } from 'react-i18next';
import { SmallSpinner } from '../common/';

const OrdersToReturn = () => {
  const { wareclouds, ordersToReturnFilters: filters } = useSelector(
    (state) => state.orders
  );

  const { t } = useTranslation(['description']);

  const loadMoreBottomRef = useRef(true);
  const loadMoreTopRef = useRef(true);

  const scrollRef = useRef(null);

  const [orders, setOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const dispatch = useDispatch();

  const [
    {
      isLoading,
      orders: ordersToReturn,
      totalPages,
      bottomInView,
      topInView,
      count,
    },
  ] = useRefreshOrder({
    apiCall: getOrdersToReturn,
    filters,
  });

  useEffect(() => {
    dispatch(setToReturnCounter(count));
  }, [dispatch, count]);

  // Hooks runs whenever orders are fetched
  useEffect(() => {
    const updateOrders = async () => {
      const ordersCopy = [...ordersToReturn];

      if (!loadMoreTopRef.current) {
        // Enters this statement if user tried to load orders in the top side of the scroll
        ordersCopy[14].ref = scrollRef;
        await setOrders(ordersCopy);

        scrollRef.current.scrollIntoView();
        loadMoreTopRef.current = true;
      } else if (!loadMoreBottomRef.current) {
        // Enters this statement if user tried to load orders in the bottom side of the scroll
        ordersCopy[14].ref = scrollRef;
        await setOrders(ordersCopy);

        scrollRef.current.scrollIntoView();
        loadMoreBottomRef.current = true;
      } else {
        // Enters this if in the first statement or when other filters excluding "page" are set
        await setOrders(ordersCopy);
      }

      setOrdersLoading(false);
      setIsLoadingMore(false);
    };

    setOrdersLoading(true);
    if (!isLoading && ordersToReturn && ordersToReturn.length > 0) {
      updateOrders();
    } else if (!isLoading && ordersToReturn && ordersToReturn.length === 0) {
      setOrders([]);
      setOrdersLoading(false);
    }
  }, [ordersToReturn, isLoading]);

  // If last card is inside the viewPort, calls hook above to fetch more orders
  useEffect(() => {
    const { page, ...rest } = filters;

    if (bottomInView && loadMoreBottomRef.current) {
      loadMoreBottomRef.current = false;

      setIsLoadingMore(true);
      dispatch(
        setOrdersToReturnFilters({
          page: filters.page + 1,
          ...rest,
        })
      );
    }
  }, [bottomInView, filters, totalPages, dispatch]);

  // If last card is inside the viewPort, calls hook above to fetch more orders
  useEffect(() => {
    const { page, ...rest } = filters;

    // Check if top card is in viewport and the loadMore ref is true
    if (topInView && loadMoreTopRef.current) {
      loadMoreTopRef.current = false;

      setIsLoadingMore(true);
      dispatch(
        setOrdersToReturnFilters({
          page: filters.page - 1,
          ...rest,
        })
      );
    }
  }, [topInView, filters, totalPages, dispatch]);

  // Toggle card info display
  const toggleShowMoreInfo = (payload) => {
    const ordersCopy = [...orders];

    ordersCopy[payload].showMore = ordersCopy[payload].showMore ? false : true;

    setOrders(ordersCopy);
  };

  // Return text/spinner depending on load and result status
  const setPlaceholder = () => {
    const message =
      !filters.filter && !filters.id_warecloud
        ? t('orders.return.no_orders', { ns: 'description' })
        : t('orders.return.no_search_orders', { ns: 'description' });

    return (
      <Card className="order-card text-center py-3">
        {ordersLoading ? (
          <SmallSpinner />
        ) : (
          <>
            <Row className="w-100">
              <SentimentDissatisfiedIcon
                sx={{ fontSize: 60 }}
                className="text-secondary w-100 mb-2"
              />
            </Row>
            <p className="m-auto">{message}</p>
          </>
        )}
      </Card>
    );
  };

  const handleReturnOrderPrompt = ({ order }) => {
    dispatch(
      setOrdersModalConfig({
        titleOpening: t('orders.return.modal.wish_return'),
        show: true,
        order,
        modalType: 'toReturn',
      })
    );
  };

  return (
    <>
      <Row>
        <FilterSelect
          name="id_warecloud"
          handleChange={(e) => {
            const { id_warecloud, ...rest } = filters;
            dispatch(
              setOrdersToReturnFilters({
                id_warecloud: e.target.value,
                ...rest,
              })
            );
          }}
          defaultLabel="Wareclouds"
          currentValue={filters.id_warecloud}
          payload={wareclouds}
        />
      </Row>
      <Row className="cards-container">
        {isLoadingMore && !loadMoreTopRef.current && <SmallSpinner />}

        {orders.length < 1 && setPlaceholder()}

        {orders.map((order, index) => (
          <OrderCard
            view="toReturn"
            order={order}
            index={index}
            toggleShowMoreInfo={toggleShowMoreInfo}
            handlePrompt={handleReturnOrderPrompt}
            buttonText="Devolver"
            cardUser={{
              label: 'Warecloud',
              value: `${order.warecloud_name} ${getEmojiTier(
                order.warecloud_id_tier
              )}`,
            }}
            outerAttributes={['warecloud_address', 'warecloud_city']}
            innerAttributes={['warecloud_phone']}
            key={`${index}-${order.order_code}-return`}
            isPending={order.id_order_transfer_confirmation}
          />
        ))}

        {isLoadingMore && !loadMoreBottomRef.current && <SmallSpinner />}
      </Row>
    </>
  );
};

export default OrdersToReturn;
